import { defineSlotRecipe } from "@pandacss/dev";
import {
  DECORATIVE_COLORS,
  getDecorativeColorToken,
} from "./utils/getDecorativeColorToken";

export const aromaTag = defineSlotRecipe({
  slots: ["container", "removeTrigger"],
  jsx: ["Tag"],
  className: "aroma-tag",
  base: {
    container: {
      borderRadius: "s",
      gap: "gap.1",
      textStyle: "mono.s.regular",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      wordBreak: "keep-all",
      textWrap: "nowrap",
      userSelect: "none",
    },
    removeTrigger: {
      borderRadius: "xs",
      cursor: "pointer",
      _aromaticonChildren: {
        fontSize: "{sizes.2xs}",
      },
    },
  },
  variants: {
    hierarchy: {
      subtlest: {
        container: {
          boxShadow: "inset 0 0 0 {borderWidths.m} var(--shadow-border-color)",
          backgroundColor: "background.neutral.base.subtlest",
        },
      },
      subtle: {
        removeTrigger: {
          _hover: {
            color: "content.neutral.reverse.subtle",
          },
        },
      },
      bold: {
        container: {
          color: "content.neutral.reverse.bold",
        },
        removeTrigger: {
          color: "content.neutral.reverse.subtle",
        },
      },
      bolder: {
        container: {
          color: "content.neutral.reverse.bold",
        },
        removeTrigger: {
          color: "content.neutral.reverse.subtle",
        },
      },
    },
    size: {
      small: {
        container: {
          minHeight: "s",
          px: "padding.1,5",
          py: "padding.1",
          _aromaticonChildren: {
            fontSize: "{sizes.2xs}",
          },
        },
        removeTrigger: {
          width: "xs",
          height: "xs",
          padding: "padding.0,5",
          mr: "-padding.1",
          my: "-padding.1",
        },
      },
      large: {
        container: {
          minHeight: "m",
          px: "padding.2",
          py: "padding.1,5",
          _aromaticonChildren: {
            fontSize: "{sizes.xs}",
          },
        },
        removeTrigger: {
          width: "s",
          height: "s",
          padding: "padding.1",
          mr: "-padding.1",
          my: "-padding.1,5",
        },
      },
    },
    iconOnly: {
      true: {},
    },
    colorTheme: {
      brand: {},
      neutral: {},
      red: {},
      orange: {},
      yellow: {},
      green: {},
      blue: {},
      purple: {},
      pink: {},
      reversed: {}, //only for whiteLabel Theme
      disabled: {}, // TODO - add all variants
    },
  },
  compoundVariants: [
    {
      hierarchy: "subtlest",
      colorTheme: "reversed",
      css: {
        container: {
          _withinWhiteLabelTheme: {
            bgColor: "transparent",
            "--shadow-border-color": "colors.border.neutral-on-brand.subtle",
            color: "content.neutral.subtle",
          },
        },
        removeTrigger: {
          _withinWhiteLabelTheme: {
            _hover: {
              bgColor: "background.neutral-on-brand.base.subtler-hover",
            },
          },
        },
      },
    },
    {
      hierarchy: "subtlest",
      colorTheme: "brand",
      css: {
        container: {
          "--shadow-border-color": "colors.border.brand-bold",
          color: "content.brand.bold",
          _withinWhiteLabelTheme: {
            "--shadow-border-color": "colors.border.brand.brand-brand",
            color: "content.brand.subtle",
          },
        },
        removeTrigger: {
          _hover: {
            bgColor: "background.brand.subtle-hover",
          },
        },
      },
    },
    {
      hierarchy: "subtlest",
      colorTheme: "neutral",
      css: {
        container: {
          "--shadow-border-color": `colors.${getDecorativeColorToken("border", "neutral")}`,
          color: "content.neutral.base.subtle",
          _withinWhiteLabelTheme: {
            "--shadow-border-color": "colors.border.neutral.bold",
          },
        },
        removeTrigger: {
          _hover: {
            bgColor: "background.decorative.neutral.hover-subtle",
          },
        },
      },
    },
    ...DECORATIVE_COLORS.map((color) => ({
      hierarchy: "subtlest" as const,
      colorTheme: color,
      css: {
        container: {
          "--shadow-border-color": `colors.${getDecorativeColorToken("border", color)}`,
          color: getDecorativeColorToken("content", color),
        },
        removeTrigger: {
          _hover: {
            bgColor: getDecorativeColorToken(
              "background",
              color,
              "hover-subtle",
            ),
          },
        },
      },
    })),
    {
      hierarchy: "subtle",
      colorTheme: "reversed",
      css: {
        container: {
          _withinWhiteLabelTheme: {
            backgroundColor: "background.neutral-on-brand.base.subtle",
            color: "content.neutral.bold",
          },
        },
        removeTrigger: {
          color: "inherit",
          _hover: {
            bgColor: "background.brand.bold",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "brand",
      css: {
        container: {
          backgroundColor: "background.brand.subtle",
          color: "content.brand.bold",
          _withinWhiteLabelTheme: {
            color: "content.brand.subtle",
          },
        },
        removeTrigger: {
          _hover: {
            bgColor: "background.brand.bold",
          },
        },
      },
    },
    {
      hierarchy: "subtle",
      colorTheme: "neutral",
      css: {
        container: {
          backgroundColor: "background.decorative.neutral.base-subtle",
          color: "content.neutral.base.subtle",
        },
        removeTrigger: {
          _hover: {
            bgColor: "background.decorative.neutral.hover-bold",
          },
        },
      },
    },
    ...DECORATIVE_COLORS.map((color) => ({
      hierarchy: "subtle" as const,
      colorTheme: color,
      css: {
        container: {
          bgColor: getDecorativeColorToken("background", color, "base-subtle"),
          color: getDecorativeColorToken("content", color),
        },
        removeTrigger: {
          _hover: {
            bgColor: getDecorativeColorToken(
              "background",
              color,
              "hover-bolder",
            ),
          },
        },
      },
    })),
    {
      colorTheme: "reversed",
      hierarchy: "bold",
      css: {
        container: {
          _withinWhiteLabelTheme: {
            backgroundColor: "background.neutral-on-brand.base.bold",
            color: "content.brand.bold",
          },
        },
        removeTrigger: {
          color: "inherit",
          _hover: {
            bgColor: "background.brand.subtle-hover",
          },
        },
      },
    },
    {
      hierarchy: "bold",
      colorTheme: "brand",
      css: {
        container: {
          backgroundColor: "background.brand.bold",
          _withinWhiteLabelTheme: {
            color: "content.neutral.bold",
          },
        },
        removeTrigger: {
          color: "inherit",
          _hover: {
            bgColor: "background.brand.bold-hover",
            _withinWhiteLabelTheme: {
              bgColor: "background.neutral-on-brand.base.subtle-hover",
            },
          },
        },
      },
    },
    {
      hierarchy: "bold",
      colorTheme: "neutral",
      css: {
        container: {
          backgroundColor: "background.decorative.neutral.base-bold",
          _withinWhiteLabelTheme: {
            backgroundColor: "background.decorative.neutral.base-bolder",
          },
        },
        removeTrigger: {
          _hover: {
            bgColor: "background.decorative.neutral.hover-bolder",
            _withinWhiteLabelTheme: {
              bgColor: "background.decorative.neutral.hover-subtle",
              color: "content.neutral.base.bold",
            },
          },
        },
      },
    },
    ...DECORATIVE_COLORS.map((color) => ({
      hierarchy: "bold" as const,
      colorTheme: color,
      css: {
        container: {
          bgColor: getDecorativeColorToken("background", color, "base-bold"),
        },
        removeTrigger: {
          _hover: {
            bgColor: getDecorativeColorToken(
              "background",
              color,
              "hover-bolder",
            ),
          },
        },
      },
    })),
    {
      hierarchy: "bolder",
      colorTheme: "brand",
      css: {
        container: {
          backgroundColor: "background.brand.bold-hover",
        },
        removeTrigger: {
          _hover: {
            color: "content.brand.bold-hover",
            bgColor: "background.brand.subtle-hover",
          },
        },
      },
    },
    {
      hierarchy: "bolder",
      colorTheme: "disabled",
      css: {
        container: {
          backgroundColor: "background.neutral.disable.bold",
          color: "content.neutral.base.disable",
        },
        removeTrigger: {
          _hover: {
            pointerEvents: "none",
            color: "content.neutral.base.disable",
            bgColor: "background.neutral.disable.bold",
          },
        },
      },
    },
    ...[...DECORATIVE_COLORS, "neutral" as const].map((color) => ({
      hierarchy: "bolder" as const,
      colorTheme: color,
      css: {
        container: {
          bgColor: getDecorativeColorToken("background", color, "base-bolder"),
        },
        removeTrigger: {
          _hover: {
            bgColor: getDecorativeColorToken(
              "background",
              color,
              "hover-subtle",
            ),
            color:
              color === "neutral"
                ? "content.neutral.base.bold"
                : getDecorativeColorToken("content", color),
          },
        },
      },
    })),
    {
      iconOnly: true,
      size: "small",
      css: {
        container: { p: "padding.1", minWidth: "s" },
        removeTrigger: { mr: "-padding.0,5" },
      },
    },
    {
      iconOnly: true,
      size: "large",
      css: {
        container: { p: "padding.1,5", minWidth: "m" },
        removeTrigger: { mr: "-padding.0,5" },
      },
    },
  ],
  defaultVariants: {
    size: "small",
  },
});
